import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Key, Copy, ArrowRight, LogIn } from "lucide-react";

export default function APIKeySection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [keyName, setKeyName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!keyName.trim()) {
      alert("Please enter a name for your API key");
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/api-keys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify({ name: keyName }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setGeneratedKey(data);
        setKeyName("");
      } else {
        alert(data.detail || "Failed to generate API key");
      }
    } catch (error) {
      console.error("Error generating API key:", error);
      alert("Failed to generate API key. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (generatedKey?.key) {
      navigator.clipboard.writeText(generatedKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Not signed in - show sign in prompt
  if (!user) {
    return (
      <section id="api-key" className="border-t border-line-1 bg-void py-20 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-cyber/30 mb-6">
              <Key size={16} className="text-cyber" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-cyber">
                Get Started
              </span>
            </div>
            
            <h2 className="font-display font-black uppercase text-3xl md:text-5xl tracking-tight mb-6">
              Generate Your <span className="text-brand">API Key</span>
            </h2>
            
            <p className="text-ink-400 text-lg mb-8">
              Sign in to generate your API key and start building AI agents with persistent memory.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate("/signup")}
                className="inline-flex items-center gap-2 bg-brand text-void px-6 py-3 text-sm font-bold tracking-wider uppercase hover:shadow-[0_0_24px_rgba(0,229,255,0.45)] transition-shadow"
              >
                Create Account <ArrowRight size={18} />
              </button>
              
              <button
                onClick={() => navigate("/login")}
                className="inline-flex items-center gap-2 border border-line-2 text-ink-100 px-6 py-3 text-sm font-bold tracking-wider uppercase hover:border-cyan-400 transition-colors"
              >
                <LogIn size={18} /> Sign In
              </button>
            </div>

            <p className="text-sm text-ink-500 mt-6">
              Free tier includes 100 reflections/month • No credit card required
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Signed in - show API key generator
  return (
    <section id="api-key" className="border-t border-line-1 bg-void py-20 md:py-32">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-cyber/30 mb-6">
            <Key size={16} className="text-cyber" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-cyber">
              Get Started
            </span>
          </div>
          
          <h2 className="font-display font-black uppercase text-3xl md:text-5xl tracking-tight mb-4">
            Generate Your <span className="text-brand">API Key</span>
          </h2>
          
          <p className="text-ink-400 text-lg max-w-2xl mx-auto">
            Create an API key to start integrating MnemonicAI into your agents.
          </p>
        </div>

        {!generatedKey ? (
          // Generate form
          <div className="max-w-xl mx-auto">
            <div className="border border-line-1 bg-surface p-8">
              <label className="block text-sm text-ink-300 mb-2">
                API Key Name
              </label>
              <input
                type="text"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="Production Key"
                className="w-full bg-void border border-line-2 px-4 py-3 text-ink-100 text-sm focus:border-cyber focus:outline-none mb-4"
              />
              
              <button
                onClick={handleGenerate}
                disabled={generating || !keyName.trim()}
                className="w-full bg-brand text-void px-6 py-3 text-sm font-bold tracking-wider uppercase hover:shadow-[0_0_24px_rgba(0,229,255,0.45)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? "Generating..." : "Generate API Key"}
              </button>

              <p className="text-xs text-ink-500 mt-4 text-center">
                You can manage all your keys in the{" "}
                <Link to="/dashboard" className="text-cyan-400 hover:underline">
                  Dashboard
                </Link>
              </p>
            </div>
          </div>
        ) : (
          // Show generated key
          <div className="max-w-3xl mx-auto">
            <div className="border-2 border-terminal bg-terminal/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 border border-terminal flex items-center justify-center flex-shrink-0">
                  <Key size={24} className="text-terminal" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-terminal mb-2">
                    API Key Generated
                  </h3>
                  <p className="text-sm text-ink-300 mb-1">
                    <strong className="text-terminal">IMPORTANT:</strong> Copy this key now. 
                    You won't be able to see it again.
                  </p>
                  <p className="text-xs text-ink-500">
                    Key name: {generatedKey.name}
                  </p>
                </div>
              </div>

              <div className="bg-void border border-line-2 p-4 mb-4">
                <div className="flex items-center justify-between gap-4">
                  <code className="text-terminal font-mono text-sm break-all flex-1">
                    {generatedKey.key}
                  </code>
                  <button
                    onClick={handleCopy}
                    className="flex-shrink-0 p-2 border border-terminal text-terminal hover:bg-terminal hover:text-void transition-colors"
                    title="Copy to clipboard"
                  >
                    <Copy size={20} />
                  </button>
                </div>
              </div>

              {copied && (
                <p className="text-terminal text-sm mb-4 text-center animate-pulse">
                  ✓ Copied to clipboard!
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setGeneratedKey(null)}
                  className="flex-1 bg-brand text-void px-6 py-3 text-sm font-bold tracking-wider uppercase hover:shadow-[0_0_24px_rgba(0,229,255,0.45)] transition-shadow"
                >
                  Generate Another Key
                </button>
                
                <Link
                  to="/dashboard"
                  className="flex-1 text-center border border-line-2 text-ink-100 px-6 py-3 text-sm font-bold tracking-wider uppercase hover:border-cyan-400 transition-colors"
                >
                  Go to Dashboard
                </Link>
              </div>

              <div className="mt-6 pt-6 border-t border-line-2">
                <p className="text-xs text-ink-500 mb-3">
                  <strong className="text-ink-300">Quick Start:</strong>
                </p>
                <div className="bg-void/50 border border-line-2 p-3 font-mono text-xs text-ink-300">
                  <div className="text-terminal mb-1"># Python</div>
                  pip install mnemonic-sdk
                  <div className="text-terminal mt-2 mb-1"># JavaScript</div>
                  npm install @mnemonicai.official/sdk-js
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
